<script setup lang="ts">
import { useModalProfileStore } from '~/stores/modal'

const colorMode = useColorMode()

const { openModal } = useModalProfileStore()
const appConfig = useAppConfig()

const authStore = useAuthStore()
const accountManagement = () => $keycloak.accountManagement()

const isDark = computed({
  get() {
    return colorMode.value === 'dark'
  },
  set() {
    colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark'
  }
})

const dropdownItems = computed(() => [
  [
    {
      label: useAuthStore().loggedUser?.name,
      slot: 'account',
      avatar: {
        src: useAuthStore().loggedUser?.photo_url
      }
    }
  ],
  [
    {
      label: 'Profil',
      icon: 'heroicons:user',
      click: () => {
        openModal()
      }
    },
    {
      label: 'Mode Tema',
      icon: isDark.value ? appConfig.ui.icons.dark : appConfig.ui.icons.light,
      click: () => {
        isDark.value = !isDark.value
      }
    },
    ...(useAuthStore().loggedUser?.is_superadmin
      ? [
          {
            label: 'User Approval',
            icon: 'heroicons:check-circle',
            to: '/admin/user-approval'
          },
          {
            label: 'Manage User',
            icon: 'heroicons:users',
            to: '/admin/manage-user'
          },
          {
            label: 'Manage Hak Akses',
            icon: 'heroicons:shield-check',
            to: '/admin/manage-access'
          },
          {
            label: 'Manage Content',
            icon: 'heroicons:document-text',
            to: '/admin/manage-content'
          },
          {
            label: 'Log Open API',
            icon: 'heroicons:list-bullet',
            to: '/admin/log-openapi'
          },
          {
            label: 'Import User',
            icon: 'carbon:user-follow',
            to: '/admin/user-import'
          }
        ]
      : []),
    {
      label: 'Manage API Key',
      icon: 'heroicons:key',
      to: '/manage-api-key'
    },
    {
      label: 'Manage Akun',
      icon: 'heroicons:cog',
      click: accountManagement
    },
    {
      label: 'Notifikasi',
      icon: 'heroicons:bell',
      to: '/notification'
    }
  ],
  [
    {
      label: 'Logout',
      icon: 'i-heroicons-arrow-left-start-on-rectangle',
      class: 'hover:bg-red-500/10 dark:hover:bg-red-500/10',
      labelClass: 'text-red-600 dark:text-red-500',
      iconClass: 'text-red-600 dark:text-red-500',
      click: () => {
        logout()
      }
    }
  ]
])

const { $keycloak } = useNuxtApp()

const login = () => $keycloak.login()
const logout = () => {
  Object.keys(localStorage).forEach(key => {
    if (key.toLowerCase().includes('grid')) {
      localStorage.removeItem(key)
    }
  })
  $keycloak.logout({ redirectUri: location.origin + '/' })
}
const appRoles = computed(() => [
  {
    name: 'KMS (Knowledge Management System)',
    role: authStore.loggedUser ? (lmsRoleLabelMap[authStore.loggedUser.lms_role] ?? null) : null
  }
])
</script>

<template>
  <div
    v-if="!$keycloak.authenticated"
    class="flex items-center gap-2"
  >
    <UColorModeButton size="sm" />
    <UButton
      label="Login OK BPSDM"
      color="primary"
      variant="solid"
      trailing-icon="i-heroicons-arrow-right-20-solid"
      class="hidden rounded-full lg:flex"
      @click="login"
    />
  </div>
  <UDropdown
    v-else
    :items="dropdownItems"
    :popper="{ arrow: true, placement: 'bottom-end' }"
    :ui="{
      item: {
        icon: { base: 'mr-1' },
        avatar: { base: 'mr-1' }
      }
    }"
  >
    <template #account="{ item }">
      <div class="w-full text-left">
        <UPopover
          mode="hover"
          class="w-full p-0"
        >
          <template #default>
            <UCard
              :ui="{
                body: { padding: '' }
              }"
              class="w-full"
            >
              <div class="flex items-center justify-start gap-5 p-2.5">
                <UAvatar
                  size="sm"
                  :src="useAuthStore().loggedUser?.photo_url"
                  alt="Profile Picture"
                />
                <div class="flelx flex-col">
                  <div class="text-md font-bold">{{ item.label }}</div>
                  <div class="text-xs font-light">
                    {{ useAuthStore().loggedUser.is_superadmin ? 'Super Admin' : 'User' }}
                  </div>
                </div>
              </div>
            </UCard>
          </template>

          <template #panel>
            <ul class="mt-2 space-y-2">
              <li
                v-for="app in appRoles"
                :key="app.name"
              >
                <div class="m-2.5">
                  <div class="text-sm font-semibold">{{ app.name }}</div>
                  <div class="my-2 text-xs font-light">{{ app.role }}</div>
                </div>

                <UDivider v-if="appRoles.length > 1" />
              </li>
            </ul>
          </template>
        </UPopover>
      </div>
    </template>
    <UButton
      variant="ghost"
      size="sm"
    >
      <UAvatar
        size="sm"
        :src="useAuthStore().loggedUser?.photo_url"
        alt="Profile Picture"
      />
    </UButton>
  </UDropdown>
</template>
